/* eslint-disable max-lines */
import $ from 'jquery';
import _ from 'underscore';
import BaseView from 'picnic/core/views/Base';
import Locations from 'app/cms/products/views/Locations';
import Filter from 'app/cms/products/views/Filter';
import Slideshow from 'app/cms/products/views/Slideshow';
import FormField from 'app/generic/form/views/FormField';
import Template from 'app/cms/products/views/Product.html';
import {i18next} from 'picnic/core/utils/i18next';
import {geolocation, location, subcategories} from 'app/cms/products/utils/api';
import {get, set} from 'app/cms/products/utils/cookie';
import {price} from 'app/cms/products/utils/price';


const
	template = _.template(Template),
	ERROR_MESSAGE = i18next.t('There are currently no ads available.'),
	EMPTY_MESSAGE = i18next.t('There were not found any ads.'),
	COOKIE = 'kleinanzeigen_location',
	SELECTOR_CATEGORY = '.category',
	SELECTOR_ADVERTISE = '.advertise',
	SELECTOR_ZIP = '.zip',
	CLASS_ACTIVE = 'is-active'
;


class View extends BaseView {

	get category() {
		return this.$el.find(SELECTOR_CATEGORY).first().text().trim();
	}

	set category(name) {
		this.$el.find(SELECTOR_CATEGORY).text(name.trim());
	}

	get categoryId() {
		return this.$el.data('category') || '';
	}

	set categoryId(id) {
		this.$el.data('category', id);
		this.$el.find(SELECTOR_ADVERTISE).attr('href', `${this._advertise}#?path=${this.parentId ? `${this.parentId}/${id}` : id}&isParent=undefined`);
	}

	get zip() {
		return this.$zip.val();
	}

	set zip(zip) {
		this.$zip.val(zip.trim());
	}

	get location() {
		this._location = this._location || this.$el.data('endpoint-location') || '';
		return this._location;
	}

	get subcategories() {
		this._subcategories = this._subcategories || this.$el.data('endpoint-subcategories') || '';
		return this._subcategories;
	}

	get url() {
		this._url = this._url || this.$el.data('endpoint-ads') || '';
		return this._url;
	}

	get fieldsObject() {
		return {
			c_sfc_id: this.categoryId,
			c_sfc_n: this.category,
			l_c_id: this.locationId,
			l_c_n: this.zip,
			l_pcid: this.zip.replace(/\D/g, ''),
		};
	}

	render() {
		super.render();

		// Store default category
		this._category = this.category;
		this._categoryId = this.categoryId;
		this._advertise = this.$el.find(SELECTOR_ADVERTISE).attr('href')?.replace(/#.*$/, '');

		// Initialize slideshow
		this.slideshow = new Slideshow({
			context: this.context,
			el: this.$el
		}).render();

		// Initialize zip
		this.$zipcontainer = this.$el.find(SELECTOR_ZIP);
		this.$zip = this.$zipcontainer.find('input').prop('autocomplete', 'off');

		// Initialize filter
		this._getFilter();

		// Use location and products API
		const zip = get(COOKIE);
		if (zip) {
			this._setZip(...zip.split('|'));
		} else {
			geolocation(this.location).then(data => {
				(data) ? this._setZip(data.id, data.name) : this._getProducts();
			});
		}

		if (this.$zipcontainer.length) {
			new FormField({el: this.$zipcontainer, context: this.context}).render();
		}

		return this;
	}

	events() {
		return {
			'change select': '_onFilter',
			'submit form': '_onSubmit',
			'keyup input': '_onInput',
			'blur input': '_onBlur',
			'click .categories button': '_onCategoryClick',
			'click .locations li': '_onLocationClick',
			'click .advertise': '_onAdvertiseClick',
			'click .more': '_onMoreClick',
			'click .product a:not(.more)': '_onProductClick',
			'mouseleave': '_removeLocations'
		};
	}

	/**
	 * Update products data
	 * @param {object} data
	 */
	update(data) {
		// Error
		if (!data || !data.ads) {
			this._showError();
			return;
		}

		// Show empty message
		if (!data.ads.length) {
			this._showError(EMPTY_MESSAGE);
			return;
		}

		// Clear
		this._removeError();
		this._clearSlideshow();

		// Add product slides
		data.ads.forEach(el => {
			this.slideshow.slideshow.slickAdd(template({
				title: el.title,
				image: el.picture,
				link: el.link,
				price: price(el.price, el.price_type, el.price_currency)
			}));
		});

		// Add more slide
		if (data.link) {
			this.slideshow.slideshow.slickAdd(template({
				link: data.link,
				category: this.category
			}));
		}
	}

	/**
	 * Update zip id and name
	 * @param {string} [id='']
	 * @param {string} [name='']
	 */
	_setZip(id = '', name = '') {
		var update = (id !== this.locationId);

		this.zip = name;
		this.locationId = id;
		this._zipcode = name;

		// Only store valid zipcodes otherwise remove cookie
		const re = /^\d+$/;
		set(COOKIE, id + '|' + name, (name && id && re.test(id)) ? 1 : 0);

		// Update products
		if (update) {
			this._getProducts();
		}
	}

	/**
	 * Use products API
	 */
	_getProducts() {
		if (!this.url) {
			return;
		}

		this._removeLocations();

		$.get(
			this.url,
			{
				'category_id': this.categoryId,
				'location_id': this.locationId
			},
			data => this.update(data)
		).fail(() => {
			this._showError();
		});
	}

	/**
	 * Use subcategories API
	 */
	_getFilter() {
		// Destroy filter view
		if (this.filter) {
			this.filter.destroy();
		}

		subcategories(this.subcategories, this.categoryId).then(data => {
			if (!data || !data.success || !data.subcategories.length) {
				return;
			}

			// Render filter view
			this.filter = new Filter({
				el: this.$el.find('form').first(),
				subcategories: data.subcategories,
				context: this.context
			}).render();
			new FormField({el: this.$el.find('.filter'), context: this.context}).render();
		});
	}

	/**
	 * Display locations list
	 * @param {object} data
	 */
	_showLocations(data) {
		this._removeLocations();

		if (!data || !data.success || !data.locations.length) {
			return;
		}

		this.locations = new Locations({
			el: this.$zipcontainer,
			locations: data.locations,
			context: this.context
		}).render();
	}

	/**
	 * Remove locations list
	 */
	_removeLocations() {
		if (this.locations) {
			this.locations.destroy();
		}
	}

	/**
	 * Display error message
	 * @param {string} [message=ERROR_MESSAGE]
	 */
	_showError(message = ERROR_MESSAGE) {
		if (!this._error || !this._error.length) {
			this._error = $('<p class="error"></p>');
			this._error.insertAfter(this.$el.find('.products'));
		}

		delete this.locationId;
		this._clearSlideshow();
		this._error.text(message);
	}

	/**
	 * Remove error message
	 */
	_removeError() {
		if (this._error && this._error.length) {
			this._error.remove();
			delete this._error;
		}
	}

	/**
	 * Clear slideshow
	 */
	_clearSlideshow() {
		this.slideshow.slideshow.slickRemove(null, null, true);
		if (this.slideshow.slideshow.unslicked) {
			this.slideshow.slideshow.$slider.empty();
		}
	}

	/**
	 * Filter change event
	 * @param {object} e
	 */
	_onFilter(e) {
		const el = e.currentTarget;

		if (el.value) {
			this.parentId = this._categoryId;
			this.category = e.currentTarget.selectedOptions[0].innerText;
			this.categoryId = e.currentTarget.value;
		} else {
			this.parentId = undefined;
			this.category = this._category;
			this.categoryId = this._categoryId;
		}

		this._getProducts();
	}

	/**
	 * Form submit event
	 * @param {object} e
	 */
	_onSubmit(e) {
		e.preventDefault();

		// If locations dropdown is visible
		if (this.locations && this.locations.active) {
			this._setZip(this.locations.active.data('id'), this.locations.active.text());
			this.$zip.blur();
			return;
		}

		// Unset zip
		if (!this.zip) {
			this._setZip();
			return;
		}

		// Get first location if zip id is missing
		if (this.zip !== this._zipcode) {
			location(this.location, this.zip.replace(/\D/g, '')).then(data => {
				// Invalid zip
				if (!data || !data.locations || !data.locations.length) {
					this._showError(EMPTY_MESSAGE);
					return;
				}

				this._setZip(data.locations[0].id, data.locations[0].name);
				this.$zip.blur();
			});
		}
	}

	/**
	 * Input change event
	 * @param {object} e
	 */
	_onInput(e) {
		this._input = this._input || '';

		if (e.currentTarget.value !== this._input) {
			location(this.location, e.currentTarget.value).then(data => this._showLocations(data));
			this._input = e.currentTarget.value;
		}
	}

	/**
	 * Input blur event
	 */
	_onBlur() {
		const zip = this.$zip[0];

		// Revert input cursor position
		if (zip.setSelectionRange) {
			zip.setSelectionRange(0, 0);
		}
	}

	/**
	 * Location click event
	 * @param {object} e
	 */
	_onLocationClick(e) {
		this._setZip($(e.currentTarget).data('id'), e.currentTarget.innerText);
		this.$zip.blur();
		this._removeLocations();
	}

	/**
	 * Category click event
	 * @param {object} e
	 */
	_onCategoryClick(e) {
		const
			$el = $(e.currentTarget),
			id = $el.data('category'),
			parent = $el.data('parent-category')
		;

		if (id === this.categoryId) {
			return;
		}

		this.parentId = parent;
		this.category = $el.text();
		this.categoryId = id;

		// Override default category
		this._category = this.category;
		this._categoryId = this.categoryId;

		this._getProducts();
		this._getFilter();

		this.$el.find('.categories button').removeClass(CLASS_ACTIVE);
		$el.addClass(CLASS_ACTIVE);
	}

	_onAdvertiseClick(e) {
		this.context.dispatch('products:link', {
			action: 'ThemenToPostAd',
			fieldsObject: {
				...this.fieldsObject,
				button_placement: 'below ads category',
				button_text: e.currentTarget.innerText,
				cta_button: 'button',
			}
		});
	}

	_onMoreClick(e) {
		this.context.dispatch('products:link', {
			action: 'ThemenToSRP',
			fieldsObject: {
				...this.fieldsObject,
				button_placement: 'inside ads category',
				button_text: e.currentTarget.innerText,
				cta_button: 'text',
			}
		});
	}

	_onProductClick(e) {
		this.context.dispatch('products:link', {
			action: 'ThemenToVIP',
			fieldsObject: {
				...this.fieldsObject,
				button_placement: 'inside ads category',
				button_text: e.currentTarget.innerText,
				ad_id: e.currentTarget.href.replace(/.*\/(\d+).*/, '$1'),
				ad_prc_amt: e.currentTarget.querySelector('.price')?.innerText,
			}
		});
	}

}

export default View;
